import React, { useState } from 'react';

const DRAG_THRESHOLD = 50;
const TRANSITION_DURATION = 300;

export const useSlider = ({ width, maxIndex }: { width: number; maxIndex: number }) => {
  const [selectedIndex, setSelectedIndex] = useState<number>(0);
  const [transX, setTransX] = useState<number>(0);

  const handlePrev = () => {
    setSelectedIndex((prev) => clamp(prev - 1, 0, maxIndex));
  };

  const handleNext = () => {
    setSelectedIndex((prev) => clamp(prev + 1, 0, maxIndex));
  };

  const handleIndicator = (index: number) => () => {
    setSelectedIndex(index);
  };

  const handleMouseDown = (event: React.MouseEvent<HTMLDivElement>) => {
    if (event.button) return;
    event.stopPropagation();
    const pageX = event.pageX;
    let animationFrameId: number;

    const mouseMove = (event: MouseEvent) => {
      cancelAnimationFrame(animationFrameId);
      const deltaX = event.pageX - pageX;
      animationFrameId = requestAnimationFrame(() => {
        if ((!selectedIndex && deltaX > 0) || (selectedIndex === maxIndex && deltaX < 0)) {
          setTransX(0);
        } else {
          setTransX(() => {
            return clamp(deltaX, -width, width);
          });
        }
      });
    };

    const mouseUp = (event: MouseEvent) => {
      const deltaX = event.pageX - pageX;

      if (deltaX < -DRAG_THRESHOLD) setSelectedIndex((prev) => clamp(prev + 1, 0, maxIndex));
      if (deltaX > DRAG_THRESHOLD) setSelectedIndex((prev) => clamp(prev - 1, 0, maxIndex));
      setTransX(0);
      document.removeEventListener('mousemove', mouseMove);
      cancelAnimationFrame(animationFrameId);
    };

    document.addEventListener('mousemove', mouseMove);
    document.addEventListener('mouseup', mouseUp, { once: true });
  };

  return {
    translateX: -selectedIndex * width + transX,
    transition: transX ? 0 : TRANSITION_DURATION,
    onMouseDown: handleMouseDown,
    arrowProps: {
      isPrevDisabled: selectedIndex === 0,
      isNextDisabled: selectedIndex === maxIndex,
      onPrev: handlePrev,
      onNext: handleNext,
    },
    indicatorProps: {
      maxIndex,
      selectedIndex,
      onClick: handleIndicator,
    },
  };
};

const clamp = (v: number, min: number, max: number) => {
  if (v < min) return min;
  if (v > max) return max;
  return v;
};
